import * as React from "react";

import "./image-editor.scss";

import {cs}                  from "../../react/chain-services";
import {keyed}               from "../../react/keyed";
import {scnv}                from "../../react/state-change-nested-value";
import {UseState}            from "../../react/use-state";
import {cnv, snv}            from "../../utils/arr-path";
import {chain}               from "../../utils/fs";
import {equalDeep, keepOnly} from "../../utils/objects";
import {isDevQuan}           from "../debug/is-dev-quan";

import {centerOfRect}    from "./common/center-of-rect";
import {downloadCanvas}  from "./common/download-draw";
import {drawToCanvas}    from "./common/draw-to-canvas";
import {eachPos}         from "./common/each-pos";
import {getColorC}       from "./common/get-color-c";
import {HistoryState}    from "./common/history-state";
import {fillColors1}     from "./draw-img/fill-colors-1";
import {getImgDataIndex} from "./draw-img/get-img-data-index";
import {getControls}     from "./get-controls";
import {ImageObject}     from "./image-object/image-object";
import {MainPanel}       from "./main-panel/main-panel";
import {CropMode}        from "./modes/crop-mode/crop-mode";
import {FilterMode}      from "./modes/filter-mode/filter-mode";
import {MaskMode}        from "./modes/mask-mode/mask-mode";
import {NormalMode}      from "./modes/normal-mode/normal-mode";

export const ImageEditor = ({url, next}) => cs(
    ["adj", (_, next) => HistoryState({
        // initValue: !isDevQuan() ? null : {
        //     //
        //     // image: {
        //     // //     center: {x: 11, y: 109.5}, // For cropping
        //     // //
        //     // //     // center: {x: 197.5, y: 97},
        //     // //     // center: {x: 191.5, y: 183.5},
        //     // //     // flip: true, // Before rotate
        //     //     rotation:  90,
        //     // //     // rotation: 270,
        //     // //     filter: {type: "grayscale"},
        //     // //     filter: {type: "tint", color: {r:255,g:255,b:255}},
        //     // },
        //     mask: {
        //         // url: require("./2.png"),
        //         url: require("./xpressdocs-squarelogo-1439494830995.png"),
        //         // url: require("./white.jpg"),
        //         // scale: 2,
        //         opacity: .5,
        //     },
        //     //
        //     // vpSize: {width: 17, height: 12},
        //     // vpSize: {width: 171, height: 128},
        //
        //     // frame: {
        //     //     offset: {
        //     //         x: 0,
        //     //         y: 0,
        //     //     },
        //     //     scale: 1,
        //     // },
        //
        // },
        canSwallowLastStep: (newStep, lastStep) => equalDeep(
            keepOnly(newStep , ["image", "mask"]),
            keepOnly(lastStep, ["image", "mask"]),
        ),
        next,
    })],

    ["imgObj", ({adj}, next) => ImageObject({
        url,
        adj: {
            ...adj.value?.image,
            ...adj.value?.mask?.maskCutout?.imgCenter && {
                center: adj.value.mask.maskCutout.imgCenter,
            },
        },
        next,
    })],

    ["mask", ({adj}, next) => cs(
        ["obj", (_, next) => ImageObject({
            url: adj.value?.mask?.url,
            adj: adj.value?.mask,
            next,
        })],
        ({obj}) => next({
            obj,
            vpSize: obj && adj.value.mask.maskCutout?.vpSize,
        }),
    )],

    ["vpSize", ({adj, imgObj, mask}, next) => next(
        mask.vpSize ?? adj.value?.vpSize ?? imgObj?.rawSize
    )],

    ["mode", (_, next) => UseState({
        initValue: !isDevQuan() ? null : (
            // "crop"
            "filter"
            // "mask"
        ),
        next,
    })],
    ["modes", ({adj, vpSize, imgObj, mask, mode}, next) => Modes({adj, vpSize, imgObj, maskObj: mask.obj, mode, next})],

    ["activeMode", ({modes, mode}, next) => next(
        modes.find((m) => m.name === mode.value) ??
        modes.find((m) => m.name === "normal") // default
    )],

    ({adj, activeMode, vpSize, imgObj, mode, mask}) => {


        const frame = chain(
            adj.value?.frame,
            (_) => !activeMode.control.modFrame ? _ : chain(_, activeMode.control.modFrame),
            (_) => ({
                scale : _?.scale ?? 1,
                offset: _?.offset ?? {x: 0, y: 0},
            })
        );

        return next({
            changed: adj.canUndo,
            renderMain: ({mobile}) => MainPanel({
                objs: chain(
                    {imgObj, maskObj: mask.obj},
                    (_) => activeMode.control.modObjects?.(_) ?? _,
                    toObjects,
                ),
                vpSize: chain(
                    vpSize,
                    (_) => activeMode.control.modVpSize?.(_) ?? _,
                ),
                frame,
                renderOverlay: ({frame, desktop, vpSize}) => cs(
                    keyed(activeMode.name),
                    () => activeMode.control.renderOverlay?.({frame, vpSize, desktop}),
                ),
                mobile,
            }),

            controls: getControls({adj, mode}),
            modePanel: activeMode.control.modePanel && (
                (params) => activeMode.control.modePanel({
                    onCancel: () => mode.onChange(null),
                    ...params,
                })
            ),

            // renderPreview: ({size, className}) => PreviewCanvas({
            //     className,
            //     objs: toObjects({imgObj, maskObj: mask.obj}),
            //     size,
            //     vpSize,
            // }),

            ...exportMethods({
                vpSize,
                getObjs: () => toObjects({imgObj, maskObj: mask.obj}),
            }),
        });
    },
);

const exportMethods = ({vpSize, getObjs}) => {

    const draw = (c) => {
        const ctx = c.getContext("2d");
        const idTarget = ctx.getImageData(0, 0, c.width, c.height);
        const getColor = getColorC(getObjs(), {vpSize: {width: c.width, height: c.height}});
        eachPos(c, (p) => {
            const color = getColor(p);

            fillColors1(color, idTarget.data, getImgDataIndex(p, c.width));
        });
        ctx.putImageData(idTarget, 0, 0);
    };
    return {

        toBlob: async ({mimeString = "image/jpeg"} = {}) => {
            const c = drawToCanvas({
                size: vpSize,
                draw,
            });
            return await new Promise((resolve) => {
                c.toBlob(resolve, mimeString);
            });
        },
        toDataURL: async ({mimeString = "image/jpeg"} = {}) => {
            const c = drawToCanvas({
                size: vpSize,
                draw,
            });
            return c.toDataURL(mimeString);
        },

        download: ({mimeString = "image/jpeg"} = {}) => downloadCanvas(
            drawToCanvas({
                size: vpSize,
                draw,
            }),
            // drawToCanvas({
            //     size: {width: 1,height: 1,},
            //     draw: (c) => {
            //         const ctx = c.getContext("2d");
            //         const idTarget = ctx.getImageData(0, 0, c.width, c.height);
            //         eachPos(c, (p) => {
            //             fillColors1({r:255,g:255,b:255,a:255}, idTarget.data, getImgDataIndex(p, c.width));
            //         });
            //         ctx.putImageData(idTarget, 0, 0);
            //     },
            // }),
            {
                mimeString,
                fileName: "a.jpg",
            },
        ),
    };
};


const toObjects = ({imgObj, maskObj}) => [imgObj, maskObj ? maskObj : null].filter(v=>v);

const Modes = ({adj, vpSize, imgObj, mode, maskObj, next}) => cs(

    ["cropMode", ({}, next) => CropMode({
        getColor: vpSize && getColorC(toObjects({imgObj, maskObj}), {vpSize}),
        onDone: (rect) => {
            mode.onChange(null);
            adj.change((a) => chain(
                {
                    ...a,
                    frame: null,
                },
                (_) => {
                    const vpSize = keepOnly(rect, ["width", "height"]);
                    return chain(
                        _,
                        (_) => snv(_, ["vpSize"], vpSize),
                        (_) => cnv(_, ["mask", "maskCutout"], (cutout) => !cutout ? null : {...cutout, vpSize: null}),
                    );
                },
                (_) => snv(_, ["image", "center"], imgObj.getCenter(centerOfRect(rect), {vpSize})),
                (_) => !maskObj ? _ : snv(_, ["mask" , "center"], maskObj.getCenter(centerOfRect(rect), {vpSize})),
            ));
        },
        next,
    })],
    ["filterMode", ({}, next) => FilterMode({
        onDone: (filter) => {
            mode.onChange(null);
            adj.change((a) => chain(
                a,
                (_) => snv(_, ["mode"], null),
                (_) => snv(_, ["image", "filter"], filter),
                (_) => snv(_, ["mask" , "filter"], filter),
            ));
        },
        next,
    })],
    ["maskMode", ({}, next) => MaskMode({
        vpSize,
        onDone: (mask) => {
            mode.onChange(null);
            adj.change((a) => chain(
                a,
                (_) => snv(_, ["mode"], null),
                (_) => snv(_, ["mask"], mask),
            ));
        },
        next,
    })],
    ["normalMode", ({}, next) => NormalMode({
        onChangeFrame: (fn) => scnv(adj, ["frame"], fn),
        next,
    })],
    ["modes", ({cropMode, normalMode, filterMode, maskMode}, next) => next([
        {
            name: "crop",
            control: cropMode,
        },
        {
            name: "filter",
            control: filterMode,
        },
        {
            name: "mask",
            control: maskMode,
        },
        {
            name: "normal",
            control: normalMode,
        },
    ])],
    ({modes}) => next(modes),
);


